import {Injectable, Injector} from '@angular/core';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {FlowlineHelperService} from '../../diagram-render/helpers/flowline-helper.service';
import {IActJson, IResourceJson} from '../../diagram-data/diagram-json-handler.service';
import {DiagramUpdateService} from '../../diagram-data/diagram-update.service';
import * as _ from 'lodash';
import {ResourceCommands} from './resource.commands';
import {DragAssistService} from '../../diagram-interaction/services/drag-assist.service';
import {getActivityMinWH} from '../../diagram-render/styles/styles.util';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';

declare let mxUtils: any;

@Injectable()
export class ResourceCommandService {
  private readonly SFA_BOTTOM_MARGIN = 20;
  private readonly SFA_TOP_MARGIN = 15;
  private readonly SFA_RESOURCE_ROW_GAP = 10;
  private readonly SFA_RESOURCE_MARGIN_CORRELATION = 14;

  #deltaDict: {[key: string]: number} = {};

  constructor(
    private editorRegistrySvc: EditorRegistryService,
    private flHelper: FlowlineHelperService,
    private diagramUpdateSvc: DiagramUpdateService,
    private injector: Injector,
    private diagramTypeConfigService: DiagramTypeConfigService,
    dragSvc: DragAssistService /** 'dragSvc' Statement just for angular constructor init **/
  ) {}

  get RESOURCE_SIZE(): number {
    return this.diagramTypeConfigService.resourceSettings().size;
  }

  public injectIntoResourceCommands() {
    const resCommands = this.injector.get(ResourceCommands);

    resCommands.addService<ResourceCommandService>('serviceObj.resourceSvc', this);
  }

  public recalcActivitySize(cell, cellJson: IActJson, opts: any = {}) {
    if (this.diagramTypeConfigService.isArchitecture()) {
      this.recalcCardActivitySize(cell, cellJson, opts);
    } else {
      this.recalcUpnActivitySize(cell, cellJson, opts);
    }
  }

  private getDeltaYFromRows({
    currentRowSize,
    newRowSize,
    currentVisibleResourcesNum,
    newVisibleResourceNum
  }): number {
    // calculates n of rows before and after action
    // returns difference of height in pixels for rows

    const currentRowsAmount = this.getResourceRowsNum(currentVisibleResourcesNum, currentRowSize);
    const newRowsAmount = this.getResourceRowsNum(newVisibleResourceNum, newRowSize);
    const difference = newRowsAmount - currentRowsAmount;

    return difference * this.RESOURCE_SIZE;
  }

  private getDeltaYFromRowGaps({
    currentRowSize,
    newRowSize,
    currentVisibleResourcesNum,
    newVisibleResourceNum
  }): number {
    // calculates n of gaps between rows before and after action
    // returns difference of height in pixels for gaps

    const currentRowsAmount = this.getResourceRowsNum(currentVisibleResourcesNum, currentRowSize);
    const newRowsAmount = this.getResourceRowsNum(newVisibleResourceNum, newRowSize);

    const currentRowGapsNum = this.getGapsNumForRows(currentRowsAmount);
    const newRowGapsNum = this.getGapsNumForRows(newRowsAmount);

    const difference = newRowGapsNum - currentRowGapsNum;

    return difference * this.SFA_RESOURCE_ROW_GAP;
  }

  private getDeltaYFromMargin({
    currentRowSize,
    newRowSize,
    currentVisibleResourcesNum,
    newVisibleResourceNum,
    footer
  }): number {
    // returns difference in pixels if it is needed to add or remove margin after resources

    const footerExist = !!footer;
    const currentRowsExist = !!this.getResourceRowsNum(currentVisibleResourcesNum, currentRowSize);
    const newRowsExist = !!this.getResourceRowsNum(newVisibleResourceNum, newRowSize);
    const difference = Number(newRowsExist) - Number(currentRowsExist); // 1||0;
    const bottomDifference = !footerExist ? difference : 0;
    const bottomDelta = bottomDifference * this.SFA_BOTTOM_MARGIN;
    const topDelta = difference * this.SFA_TOP_MARGIN;

    return bottomDelta + topDelta;
  }

  private getDeltaXFromRows(payload): {minDeltaX: number; actualDeltaX: number} {
    // calculates max n of resources in a row before and after action
    // calculates delta for min width and actual width
    // returns min and actual difference in width

    const {currentRowSize, newRowSize, currentVisibleResourcesNum, newVisibleResourceNum, width} =
      payload;
    const MIN_RESOURCES_FIT = 2;
    const maxResNumToFitX = Math.floor(
      width / (this.RESOURCE_SIZE + this.SFA_RESOURCE_MARGIN_CORRELATION)
    );
    const currentMaxResPerRow = this.getActualMaxResourcesPerRow(
      currentVisibleResourcesNum,
      currentRowSize
    );
    const newMaxResPerRow = this.getActualMaxResourcesPerRow(newVisibleResourceNum, newRowSize);

    let minDeltaX = 0;
    let actualDeltaX = 0;

    switch (true) {
      case currentMaxResPerRow <= MIN_RESOURCES_FIT && newMaxResPerRow <= MIN_RESOURCES_FIT: {
        break;
      }

      case currentMaxResPerRow <= MIN_RESOURCES_FIT && newMaxResPerRow > MIN_RESOURCES_FIT: {
        minDeltaX =
          (newMaxResPerRow - MIN_RESOURCES_FIT) *
          (this.RESOURCE_SIZE + this.SFA_RESOURCE_MARGIN_CORRELATION);
        actualDeltaX =
          Math.max(newMaxResPerRow - maxResNumToFitX, 0) *
          (this.RESOURCE_SIZE + this.SFA_RESOURCE_MARGIN_CORRELATION);
        break;
      }

      case currentMaxResPerRow > MIN_RESOURCES_FIT && newMaxResPerRow <= MIN_RESOURCES_FIT: {
        minDeltaX =
          (MIN_RESOURCES_FIT - currentMaxResPerRow) *
          (this.RESOURCE_SIZE + this.SFA_RESOURCE_MARGIN_CORRELATION);
        break;
      }

      default: {
        minDeltaX =
          (newMaxResPerRow - currentMaxResPerRow) *
          (this.RESOURCE_SIZE + this.SFA_RESOURCE_MARGIN_CORRELATION);
        actualDeltaX =
          Math.max(newMaxResPerRow - maxResNumToFitX, 0) *
          (this.RESOURCE_SIZE + this.SFA_RESOURCE_MARGIN_CORRELATION);
      }
    }

    return {minDeltaX, actualDeltaX};
  }

  private getResourceRowsNum(visibleResources: number, rowSize): number {
    // returns n of rows for card

    rowSize = rowSize ?? 0;

    if (visibleResources === 0) {
      return 0;
    }

    if (rowSize === 0) {
      return 1;
    }

    const rows = visibleResources / rowSize;

    return Math.ceil(rows);
  }

  private getGapsNumForRows(rowNum: number): number {
    // return n of gaps between resource rows

    if (rowNum === 0) {
      return 0;
    }

    return rowNum - 1;
  }

  private getActualMaxResourcesPerRow(visibleResources: number, rowSize: number): number {
    // returns max n of resources in one row for card

    rowSize = rowSize ?? 0;

    if (rowSize === 0) {
      return visibleResources;
    }

    return visibleResources > rowSize ? rowSize : visibleResources;
  }

  private recalcUpnActivitySize(cell, cellJson: IActJson, opts: any = {}) {
    const {remove} = opts;
    const graph = this.editorRegistrySvc.getGraph();
    const dy = remove ? -this.RESOURCE_SIZE : this.RESOURCE_SIZE;

    const {minHeight} = getActivityMinWH(cellJson, this.diagramTypeConfigService.currentType());

    graph.getModel().setStyle(cell, mxUtils.setStyle(cell.style, 'minHeight', minHeight + dy));

    const geometry = cell.getGeometry().clone();

    geometry.height += dy;

    this.translateCellsConnectionPoints(cell, dy);
    graph.getModel().setGeometry(cell, geometry);
    this.flHelper.translateConnectedEdges([cell], {dx: 0, dy: 0}, {dx: 0, dy: dy});
  }

  private recalcCardActivitySize(cell, cellJson: IActJson, opts: any = {}) {
    const graph = this.editorRegistrySvc.getGraph();
    const {remove, rowSize, deltaKey = null} = opts;
    const deltaSize = {x: 0, y: 0};

    const withRemove = remove !== undefined;
    const currentRowSize = cellJson.resourcesPerRow;
    const newRowSize = rowSize ?? currentRowSize;

    const currentVisibleResourcesNum = (cellJson.resources || []).reduce(
      (acc, curr) => (curr.hidden || curr.main ? acc : ++acc),
      0
    );

    const newVisibleResourceNum = withRemove
      ? remove
        ? currentVisibleResourcesNum - 1
        : currentVisibleResourcesNum + 1
      : currentVisibleResourcesNum;

    const geometry = cell.getGeometry().clone();
    const calcPayload = {
      currentRowSize,
      newRowSize,
      currentVisibleResourcesNum,
      newVisibleResourceNum,
      width: geometry.width,
      footer: cellJson.footerText
    };
    const deltaYFromRows = this.getDeltaYFromRows(calcPayload);
    const deltaYFromGaps = this.getDeltaYFromRowGaps(calcPayload);
    const deltaYFromMargin = this.getDeltaYFromMargin(calcPayload);
    const {minDeltaX, actualDeltaX} = this.getDeltaXFromRows(calcPayload);

    const totalDeltaY = deltaYFromRows + deltaYFromGaps + deltaYFromMargin;

    deltaSize.y += totalDeltaY;
    deltaSize.x += minDeltaX;

    const {minWidth, minHeight} = getActivityMinWH(
      cellJson,
      this.diagramTypeConfigService.currentType()
    );
    const newMinWidth = minWidth + deltaSize.x;
    const newMinHeight = minHeight + deltaSize.y;

    graph.getModel().setStyle(cell, mxUtils.setStyle(cell.style, 'minWidth', newMinWidth));
    graph.getModel().setStyle(cell, mxUtils.setStyle(cell.style, 'minHeight', newMinHeight));

    const calculatedYDelta = newMinHeight > geometry.height ? newMinHeight - geometry.height : 0;
    const previousDelta = this.#deltaDict[deltaKey];
    const actualDeltaY = previousDelta ?? calculatedYDelta;

    if (previousDelta) {
      delete this.#deltaDict[deltaKey];
    } else if (deltaKey) {
      this.#deltaDict[deltaKey] = -calculatedYDelta;
    }

    geometry.width += actualDeltaX;
    geometry.height += actualDeltaY;
    this.translateCellsConnectionPoints(cell, actualDeltaY);
    graph.getModel().setGeometry(cell, geometry);

    this.flHelper.translateConnectedEdges(
      [cell],
      {dx: 0, dy: 0},
      {dx: deltaSize.x, dy: actualDeltaX}
    );
  }

  public move(opts) {
    const groupUpdate = this.diagramUpdateSvc.group();
    const graph = this.editorRegistrySvc.getGraph();

    if (!opts || !opts.resource || !opts.cells) {
      return;
    }

    graph.getModel().beginUpdate();

    const detachAction = this.detach({
      cell: opts.cells[0],
      resource: opts.resource,
      deltaKey: opts.deltaKey ? `${opts.deltaKey}-=${opts.cells[0].id}` : null
    });

    const attachAction = this.attach({
      cell: opts.cells[1],
      resource: opts.resource,
      position: opts.position,
      deltaKey: opts.deltaKey ? `${opts.deltaKey}-=${opts.cells[1].id}` : null
    });

    groupUpdate.add(detachAction);
    groupUpdate.add(attachAction);

    graph.getModel().endUpdate();
    groupUpdate.sendGroup();

    opts.cells.forEach(cell => cell.comp.instance.setupFromJson());
  }

  public add(opts) {
    const graph = this.editorRegistrySvc.getGraph();

    if (!opts || !opts.resource || !opts.cells) {
      return;
    }

    const groupUpdate = this.diagramUpdateSvc.group();

    graph.getModel().beginUpdate();

    opts.cells = [].concat(opts.cells); // forced cast to array

    opts.cells.forEach(cell => {
      cell.skipUndo = true;

      const attachAction = this.attach({
        cell,
        resource: opts.resource,
        position: opts.position,
        skipCalc: opts.skipCalc,
        deltaKey: opts.deltaKey ? `${opts.deltaKey}+${cell.id}` : null
      });

      if (attachAction) {
        groupUpdate.add(attachAction);
      }
    });

    graph.getModel().endUpdate();
    groupUpdate.sendGroup();

    opts.cells.forEach(cell => cell.comp.instance.setupFromJson());
  }

  public remove(opts) {
    const graph = this.editorRegistrySvc.getGraph();

    if (!opts || !opts.resource || !opts.cells) {
      return;
    }

    const groupUpdate = this.diagramUpdateSvc.group();

    graph.getModel().beginUpdate();

    opts.cells = [].concat(opts.cells);

    opts.cells.forEach(cell => {
      cell.skipUndo = true;

      const detachAction = this.detach({
        cell,
        resource: opts.resource,
        deltaKey: opts.deltaKey ? `${opts.deltaKey}+${cell.id}` : null
      });

      if (detachAction) {
        groupUpdate.add(detachAction);
      }
    });

    graph.getModel().endUpdate();

    groupUpdate.sendGroup();

    opts.cells.forEach(cell => cell.comp.instance.setupFromJson());
  }

  private attach({
    cell,
    resource,
    position,
    skipCalc = false,
    deltaKey = null
  }: {
    cell: any;
    resource: IResourceJson;
    position: number;
    skipCalc?: boolean;
    deltaKey?: string;
  }) {
    resource = _.cloneDeep(resource);
    const cellJson = cell.getJson();
    const hasVisible =
      cellJson.resources?.length > 0 && cellJson.resources.some(r => !r.hidden && !r.main);
    const mainResource = cellJson.resources?.find(res => res.main);

    if (!cellJson.resources) {
      cellJson.resources = [];
    }

    let isAttached = false;

    if (!resource.mxKey) {
      cellJson.resources.forEach(res => {
        if (res.resourceDbId === resource.id) {
          isAttached = true;
        }
      });
    }

    if (isAttached) {
      return;
    }

    resource.mxKey = this.getMxKey(cellJson.resources, resource.mxKey);
    const skip = resource.hidden || (skipCalc && !mainResource);

    if (!skip) {
      this.recalcActivitySize(cell, cellJson, {
        remove: false,
        expand: hasVisible ? 0 : 1,
        deltaKey
      });
    }

    return {
      attachResource: {
        mxKey: Number(resource.mxKey),
        activityMxKey: Number(cell.id),
        resourceDbId: resource.resourceDbId || resource.id,
        resourceName: resource.name,
        resourceType: resource.type,
        rasci: resource.rasci,
        hidden: resource.hidden,
        icon: resource.icon,
        position,
        main: resource.main
      }
    };
  }

  private detach({
    cell,
    resource,
    deltaKey = null
  }: {
    cell: any;
    resource: IResourceJson;
    deltaKey?: string;
  }) {
    const cellJson = cell.getJson();

    if (!cellJson.resources) {
      cellJson.resources = [];
    }

    const visibleCount = cellJson.resources.reduce(
      (acc, curr) => (curr.hidden || curr.main ? acc : ++acc),
      0
    );

    if (!resource.hidden) {
      this.recalcActivitySize(cell, cellJson, {
        remove: true,
        expand: visibleCount === 1 ? 2 : 0, // since it was last visible resource - decrease height
        deltaKey
      });
    }

    const resourceDbId = resource.resourceDbId || resource.id;

    const resToDetach = cellJson.resources.find(
      res => String(res.resourceDbId) === String(resourceDbId)
    );

    return {
      detachResource: {
        mxKey: Number(resToDetach.mxKey),
        activityMxKey: Number(cell.id)
      }
    };
  }

  private getMxKey(resources: Array<any>, usedKey?: number): number {
    let mxKey = 0;
    let i = 0;
    const resMap = resources.reduce((acc, curr) => {
      acc[curr.mxKey] = true;

      return acc;
    }, {});

    if (usedKey && !resMap[usedKey]) {
      return usedKey;
    }

    while (!mxKey) {
      if (!resMap[++i]) {
        mxKey = i;
      }
    }

    return mxKey;
  }

  private translateCellsConnectionPoints(cell, dy) {
    const graph = this.editorRegistrySvc.getGraph();
    const model = graph.getModel();

    if (cell.edges) {
      cell.edges.forEach(edge => {
        if (
          edge.source &&
          Number(edge.source.id) === Number(cell.id) &&
          isInRange(graph.getCellStyle(edge).exitY)
        ) {
          setNewY(edge, true);
        } else if (
          edge.target &&
          Number(edge.target.id) === Number(cell.id) &&
          isInRange(graph.getCellStyle(edge).entryY)
        ) {
          setNewY(edge, false);
        }
      });
    }

    function setNewY(edge, isSource) {
      const height = cell.geometry.height;
      const oldY = isSource ? graph.getCellStyle(edge).exitY : graph.getCellStyle(edge).entryY;
      let newY = oldY - (1 - height / (height + dy)) * oldY;

      if (newY < 0.01) {
        newY = 0.01;
      }

      if (newY > 0.99) {
        newY = 0.99;
      }

      if (isSource) {
        model.setStyle(edge, mxUtils.setStyle(edge.style, 'exitY', newY));
      } else {
        model.setStyle(edge, mxUtils.setStyle(edge.style, 'entryY', newY));
      }
    }

    function isInRange(num: number) {
      return num >= 0.01 && num <= 0.99;
    }
  }
}
